import { useState, useEffect, useRef } from "react";
import { Box, Typography, Link } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import BaseInput from "../../BaseInput";
import BaseButton from "../../BaseButton";
import { VerifyKitCode } from "../../../apollo-client/api-query";
import KitCodeHelperModal from "./KitCodeHelperModal";

export default function KitNumberStep({ data, onNext, onClose }) {
  const [kitCode, setKitCode] = useState(data.kitCode || '');
  const [confirmKitCode, setConfirmKitCode] = useState(data.kitCode || '');
  const firstInputRef = useRef(null);

  // Auto-focus first input on mount
  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);
  const [error, setError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [showHelperModal, setShowHelperModal] = useState(false);

  const [verifyKitCode] = useLazyQuery(VerifyKitCode, {
    fetchPolicy: 'network-only', // Always verify with backend, never use cache
    onCompleted: (result) => {
      setIsVerifying(false);
      if (result.verifyKitCode === true) {
        // Kit code is valid, proceed to next step
        onNext({ kitCode: kitCode.trim() });
      } else {
        setError('This kit code is not valid or has already been used. Please check and try again.');
      }
    },
    onError: (error) => {
      setIsVerifying(false);
      console.error('Kit code verification error:', error);
      setError('Unable to verify kit code. Please try again or contact support.');
    }
  });

  const handleChange = (e) => {
    setKitCode(e.target.value);
    if (error) {
      setError('');
    }
    // Clear confirm error if they now match
    if (confirmError && e.target.value.trim() === confirmKitCode.trim()) {
      setConfirmError('');
    }
  };

  const handleConfirmChange = (e) => {
    setConfirmKitCode(e.target.value);
    if (confirmError) {
      setConfirmError('');
    }
    // Clear confirm error if they now match
    if (e.target.value.trim() === kitCode.trim()) {
      setConfirmError('');
    }
  };

  const handleNext = async () => {
    // Validate kit code format
    const trimmedKitCode = kitCode.trim();
    const trimmedConfirmKitCode = confirmKitCode.trim();

    if (!trimmedKitCode) {
      setError('Kit code is required');
      return;
    }

    // Kit codes are typically 14 digits based on the example in the API docs
    if (trimmedKitCode.length < 10) {
      setError('Kit code must be at least 10 characters');
      return;
    }

    if (!trimmedConfirmKitCode) {
      setConfirmError('Please confirm the kit code');
      return;
    }

    // Check if both kit codes match
    if (trimmedKitCode !== trimmedConfirmKitCode) {
      setConfirmError('Kit codes do not match. Please check and try again.');
      return;
    }

    // Verify kit code with backend
    setIsVerifying(true);
    setError('');
    setConfirmError('');

    try {
      await verifyKitCode({
        variables: { kitCode: trimmedKitCode }
      });
    } catch (err) {
      setIsVerifying(false);
      setError('Unable to verify kit code. Please try again.');
    }
  };

  const canContinue = kitCode.trim() && confirmKitCode.trim();

  return (
    <Box>
      {/* Title */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h1"
          sx={{
            color: '#5A3BC7',
            fontSize: { xs: '24px', sm: '28px' },
            fontWeight: 600,
            mb: 1
          }}
        >
          Enter Kit Code
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#5B87EF',
            fontSize: '14px'
          }}
        >
          Please enter the DNA test Kit Code for this patient
        </Typography>
      </Box>

      {/* Kit Code Input */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body2"
          sx={{
            color: '#46545F',
            fontSize: '14px',
            fontWeight: 500,
            mb: 1
          }}
        >
          Kit Code <span style={{ color: '#E73E3E' }}>*</span>
        </Typography>
        <BaseInput
          ref={firstInputRef}
          fullWidth
          placeholder="Enter 14-digit kit code"
          value={kitCode}
          onChange={handleChange}
          error={!!error}
          helperText={error}
          disabled={isVerifying}
          inputStyles={{
            background: '#F6F9FC',
            border: error ? '1px solid #E73E3E' : '1px solid #ECEFF1',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '15px',
            fontFamily: 'monospace',
            letterSpacing: '0.5px'
          }}
        />
      </Box>

      {/* Confirm Kit Code Input */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body2"
          sx={{
            color: '#46545F',
            fontSize: '14px',
            fontWeight: 500,
            mb: 1
          }}
        >
          Confirm Kit Code <span style={{ color: '#E73E3E' }}>*</span>
        </Typography>
        <BaseInput
          fullWidth
          placeholder="Re-enter kit code to confirm"
          value={confirmKitCode}
          onChange={handleConfirmChange}
          error={!!confirmError}
          helperText={confirmError}
          disabled={isVerifying}
          inputStyles={{
            background: '#F6F9FC',
            border: confirmError ? '1px solid #E73E3E' : '1px solid #ECEFF1',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '15px',
            fontFamily: 'monospace',
            letterSpacing: '0.5px'
          }}
        />
        {!confirmError && kitCode && confirmKitCode && kitCode.trim() === confirmKitCode.trim() && (
          <Typography
            variant="caption"
            sx={{
              color: '#2E7D32',
              fontSize: '12px',
              display: 'block',
              mt: 1
            }}
          >
            Kit codes match
          </Typography>
        )}
      </Box>

      {/* Info Box */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '8px',
          border: '1px solid #5B87EF',
          background: '#F6F9FC'
        }}
      >
        <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
          The Kit Code uniquely identifies this DNA test kit and ensures results are correctly linked to the patient.
        </Typography>
      </Box>

      {/* Helper Link */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Link
          component="button"
          type="button"
          onClick={() => setShowHelperModal(true)}
          sx={{
            color: '#5B87EF',
            fontSize: '14px',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              color: '#5A3BC7'
            }
          }}
        >
          How to locate the Kit Code
        </Link>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end'
        }}
      >
        <BaseButton
          type="button"
          onClick={onClose}
          disabled={isVerifying}
          sx={{
            width: { xs: '100%', sm: '140px' },
            background: '#46545F',
            '&:hover': {
              background: '#2A3342'
            }
          }}
        >
          Cancel
        </BaseButton>
        <BaseButton
          onClick={handleNext}
          disabled={isVerifying || !canContinue}
          loading={isVerifying}
          buttonType="primary"
          sx={{
            width: { xs: '100%', sm: '180px' },
            opacity: (isVerifying || !canContinue) ? 0.5 : 1,
            cursor: (isVerifying || !canContinue) ? 'not-allowed' : 'pointer'
          }}
        >
          {isVerifying ? 'Verifying...' : 'Continue'}
        </BaseButton>
      </Box>

      {/* Helper Modal */}
      <KitCodeHelperModal
        open={showHelperModal}
        onClose={() => setShowHelperModal(false)}
      />
    </Box>
  );
}
